import { LevelModel } from 'shared/domain/level/types/model';
import { LevelOnView } from 'shared/domain/level/types/view';
import { LabelledEntity, HashMap } from 'shared/types/commonView';
import { compareObjectIds } from 'shared/utils/id/compare';
import { areObjectIdsEqual, isObjectIdString } from 'helpers/idComparison';

/**
 * Whether the level shows the documentation as its map (in whichever version
 * is current). A documentation not sent yet has no id: no level shows it.
 */
export function hasDocumentation(
  level: Pick<LevelModel, 'map'>,
  documentationId: string | undefined
): boolean {
  return isObjectIdString(documentationId) && areObjectIdsEqual(level.map?.documentationId, documentationId);
}

/**
 * Find levels on a specific site that have given documentation
 */
export function findLevelsForSiteAndDocumentation(
  levels: LevelModel[],
  siteId: string,
  documentationId: string | undefined
): LabelledEntity[] {
  return levels
    .filter((level) => level.site === siteId && hasDocumentation(level, documentationId))
    .map((level) => ({ _id: level._id, label: level.label }));
}

export function findDocumentationOnLevelsAndSites(
  levels: LevelModel[],
  sites: LabelledEntity[],
  documentationId: string | undefined
): { levelsValue: LabelledEntity[]; sitesValue: LabelledEntity[] } {
  const siteIds: string[] = [];

  const levelsValue = levels.reduce(
    (result: LabelledEntity[], level): LabelledEntity[] => {
      const isDocumentationOnLevel = hasDocumentation(level, documentationId);
      if (
        isDocumentationOnLevel &&
        !result.some((entry) =>
          compareObjectIds(entry._id, level._id, 'findDocumentationOnLevels')
        )
      ) {
        result.push({ _id: level._id, label: level.label });
        siteIds.push(level.site);
      }

      return result;
    },
    [] as LabelledEntity[]
  );

  const sitesValue = sites.reduce(
    (result: LabelledEntity[], site: LabelledEntity): LabelledEntity[] => {
      if (siteIds.includes(site._id)) {
        result.push({ _id: site._id, label: site.label });
      }
      return result;
    },
    []
  );

  return { levelsValue, sitesValue };
}

/**
 * Filter existing levelsPerSite HashMap to only include levels with given documentation
 */
export function filterLevelsPerSiteByDocumentation(
  levels: LevelModel[],
  levelsPerSite: HashMap<LevelOnView[]>,
  documentationId: string | undefined
): HashMap<LevelOnView[]> {
  // Create a Set of level IDs that have the documentation
  const levelIdsWithDoc = new Set<string>();
  
  levels.forEach((level) => {
    if (hasDocumentation(level, documentationId)) {
      levelIdsWithDoc.add(level._id);
    }
  });

  // Filter the existing levelsPerSite
  const result: HashMap<LevelOnView[]> = {};
  
  Object.keys(levelsPerSite).forEach((siteId) => {
    const filteredLevels = levelsPerSite[siteId].filter((levelOnView) =>
      levelIdsWithDoc.has(levelOnView._id)
    );
    
    if (filteredLevels.length > 0) {
      result[siteId] = filteredLevels;
    }
  });

  return result;
}
